import { Box } from '@zillow/constellation-10';

import TourCardEditable from './TourCardEditable';
import TourCardEmpty from './TourCardEmpty';

import type {
  RentalManagerExternalTourDto,
  RentalManagerVrModelDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  tourType: 'amenity' | 'singleFamily';
  isDisabled?: boolean;
  vrModels: Array<RentalManagerVrModelDto>;
  externalTours: Array<RentalManagerExternalTourDto>;
  isMultifamily: boolean;
  updateStatusMessage: (message: string, tone?: 'critical' | 'success' | 'warning' | 'info') => void;
  isRelistingFieldset?: boolean;
};

const ListingTour = ({
  tourType,
  isDisabled = false,
  vrModels = [],
  externalTours = [],
  isMultifamily,
  updateStatusMessage,
  isRelistingFieldset = false,
}: Props) => {
  const listingToursCount = vrModels.length + externalTours.length;
  const showEmptyTour = listingToursCount === 0 || isMultifamily;

  const renderTour = (
    tour: RentalManagerVrModelDto | RentalManagerExternalTourDto,
    index: number,
    isExternalTour: boolean,
  ) => {
    const isPublished = tour.status === 'published';
    const extraProps = { [isExternalTour ? 'externalTour' : 'vrModel']: tour };
    return (
      <Box
        key={`${isExternalTour ? 'externalTour' : 'vrModel'}-${isPublished ? '' : 'un'}published-${index}`}
        css={{ width: { base: '100%', md: '320px' }, marginBottom: '400' }}
      >
        <TourCardEditable
          tourType={tourType}
          hasBigBottomBorder={true}
          isDisabled={isDisabled}
          size="md"
          updateStatusMessage={updateStatusMessage}
          {...extraProps}
        />
      </Box>
    );
  };

  return (
    <>
      {listingToursCount === 0 ? (
        <TourCardEmpty
          listingToursCount={0}
          tourType={tourType}
          isDisabled={isDisabled}
          isRelistingFieldset={isRelistingFieldset}
        />
      ) : (
        <Box
          css={{
            marginTop: '200',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: { base: 'center', lg: 'flex-start' },
          }}
        >
          {vrModels
            .filter((vrModel) => vrModel.status === 'published')
            .map((vrModel, index) => renderTour(vrModel, index, false))}
          {externalTours
            .filter((externalTour) => externalTour.status === 'published')
            .map((externalTour, index) => renderTour(externalTour, index, true))}
          {vrModels
            .filter((vrModel) => vrModel.status !== 'published')
            .map((vrModel, index) => renderTour(vrModel, index, false))}
          {externalTours
            .filter((externalTour) => externalTour.status !== 'published')
            .map((externalTour, index) => renderTour(externalTour, index, true))}
          {showEmptyTour && (
            <TourCardEmpty
              tourType={tourType}
              listingToursCount={listingToursCount}
              isDisabled={isDisabled}
              isRelistingFieldset={isRelistingFieldset}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default ListingTour;
